import React, { useState, useEffect, useRef, forwardRef, useMemo, useCallback, useImperativeHandle } from 'react';
import { FaSave, FaTrash, FaTimes, FaWindowMinimize, FaSpinner, FaExternalLinkAlt, FaArrowRight, FaArrowLeft } from 'react-icons/fa';
import FormInput from './FormInput';
import { findDifferences } from '../utils/tableUtils';

const EditRecordForm = forwardRef(({
  selectedRow,
  onUpdate,
  onDelete,
  onCancel,
  onMinimize,
  columnProps = [],
  inputTypes = {},
  lookupOptions = {},
  isLoadingLookups = false,
  theme = {},
  primaryKey,
  isProcessing = false,
  onViewRelatedItems,
  relatedItemsButtonText = "Visualizza Elementi Collegati",
  formLayoutConfig,
  tableName, // ✅ AGGIUNTO: Assicurati che tableName sia passato come prop
  renderCustomFormActions, // NUOVO: Callback per renderizzare pulsanti custom
  onFieldChange, // NUOVO: Callback per side-effects al cambio di un campo
  isFieldVisible,
}, ref) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const originalDataRef = useRef({});
  const themeColor = theme?.themeColor || 'primary';

  // SPOSTATO: Tutti gli hooks PRIMA di qualsiasi return condizionale
  const formColumnProps = useMemo(() => {
    return columnProps.filter(col =>
      col &&
      (col.visibility === 1 || col.visibility === 2) &&
      !col.isPrimaryKey &&
      (!isFieldVisible || isFieldVisible(col, formData, 'edit'))
    );
  }, [columnProps, formData, isFieldVisible]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      // Se c'è un callback onFieldChange, calcola eventuali side-effects
      if (onFieldChange) {
        const sideEffects = onFieldChange(field, value, updated);
        if (sideEffects && typeof sideEffects === 'object') {
          Object.assign(updated, sideEffects);
        }
      }
      return updated;
    });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  }, [errors, onFieldChange]);

  // ✅ NUOVO: Aggiungi la validazione built-in per Test Modifiche (stessa logica di NewRecordForm)
  const validateTestModificheUsers = useCallback((formData, newErrors) => {
    // Se ci sono campi TEM_IdRichiedente_Utenti e TEM_IdEsecutore_Utenti, controllali
    if (formData.TEM_IdRichiedente_Utenti && formData.TEM_IdEsecutore_Utenti) {
      if (formData.TEM_IdRichiedente_Utenti === formData.TEM_IdEsecutore_Utenti) {
        const errorMessage = "L'utente richiedente e l'utente esecutore devono essere diversi";
        newErrors.TEM_IdRichiedente_Utenti = errorMessage;
        newErrors.TEM_IdEsecutore_Utenti = errorMessage;
        
        console.log('[EditRecordForm] VALIDAZIONE TEST MODIFICHE FALLITA: utenti duplicati');
        console.log('[EditRecordForm] Richiedente:', formData.TEM_IdRichiedente_Utenti);
        console.log('[EditRecordForm] Esecutore:', formData.TEM_IdEsecutore_Utenti);
      }
    }
    return newErrors;
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};
    formColumnProps.forEach(col => {
      if (col.required && !col.readOnly && !col.isPrimaryKey) {
        const value = formData[col.field];
        if (value === null || value === undefined || value === '') {
          newErrors[col.field] = `${col.label || col.field} è obbligatorio.`;
        }
      }
    });
    
    // ✅ NUOVO: Applica la validazione built-in per Test Modifiche
    console.log('[EditRecordForm] Applicando validazione built-in per Test Modifiche...');
    validateTestModificheUsers(formData, newErrors);
    
    console.log('[EditRecordForm] Validation errors:', newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formColumnProps, formData, validateTestModificheUsers]);

  const handleUpdateSubmit = useCallback((e) => {
    e.preventDefault();
    if (validateForm()) {
      const changes = findDifferences(originalDataRef.current, formData);
      
      // **FIX: Filtra i campi tecnici dal payload di update come fa NewRecordForm**
      const filteredChanges = {};
      
      Object.keys(changes).forEach(fieldName => {
        if (isFieldVisible && !formColumnProps.some(col => col.field === fieldName)) {
          return;
        }

        // Salta i campi tecnici che vengono gestiti automaticamente dal backend
        if (fieldName && fieldName.length > 4) {
          const afterPrefix = fieldName.substring(4); // Rimuovi i primi 4 caratteri (es: ATT_, CAM_)
          
          // **Escludi campi di audit e sistema**
          const auditFields = [
            'StatiRecord',
            'InizioValidita', 
            'FineValidita',
            'UtenteInserimento',
            'DataOraInserimento',
            'DataOraModifica', 
            'TerminaleInserimento',
            'TerminaleModifica',
            'UtenteModifica',
            'Timestamp'
          ];
          
          if (auditFields.some(auditField => afterPrefix.startsWith(auditField))) {
            return; // Salta i campi di audit
          }
        }
        
        // Includi il campo filtrato
        filteredChanges[fieldName] = changes[fieldName];
      });
      
      // **FIX: Converti stringhe vuote in null per campi numerici**
      Object.keys(filteredChanges).forEach(fieldName => {
        const col = formColumnProps.find(c => c.field === fieldName);
        if ((col?.type === 'number' || inputTypes?.[fieldName] === 'number') &&
            (filteredChanges[fieldName] === '' || filteredChanges[fieldName] === undefined)) {
          filteredChanges[fieldName] = null;
        }
      });
      const payload = { ...filteredChanges, [primaryKey]: selectedRow[primaryKey] };

      if (Object.keys(filteredChanges).length > 0) {
        console.log("Updating record with filtered changes:", payload);
        onUpdate(payload);
      } else {
        console.log("Nessuna modifica rilevata dopo il filtraggio.");
      }
    } else {
      console.log("Validation errors:", errors);
    }
  }, [validateForm, formData, primaryKey, selectedRow, onUpdate, errors, formColumnProps, inputTypes, isFieldVisible]);

  const handleDeleteClick = useCallback(() => {
    if (window.confirm(`Sei sicuro di voler eliminare questo record? (ID: ${selectedRow[primaryKey]})`)) {
      onDelete();
    }
  }, [selectedRow, primaryKey, onDelete]);

  // Esponi metodi tramite ref per il MinimizedEditForm
  useImperativeHandle(ref, () => ({
    handleSubmit: () => {
      console.log('[EditRecordForm] handleSubmit chiamato tramite ref');
      console.log('[EditRecordForm] handleUpdateSubmit:', handleUpdateSubmit);
      // Crea un evento sintetico per triggerare handleUpdateSubmit
      const syntheticEvent = {
        preventDefault: () => {}
      };
      handleUpdateSubmit(syntheticEvent);
    }
  }), [handleUpdateSubmit]);

  const renderFormInput = useCallback((col) => {
    const inputType = inputTypes[col.field] || col.type || 'text';
    const isReadOnly = col.readOnly || col.isPrimaryKey;
    
    return (
      <FormInput
        key={col.field}
        field={col.field}
        label={col.label || col.field}
        value={formData[col.field]}
        onChange={handleInputChange}
        inputType={inputType}
        lookupOptions={lookupOptions[col.lookupName || col.field]}
        isLoadingLookups={isLoadingLookups}
        placeholder={col.placeholder || ''}
        disabled={isProcessing}
        readOnly={isReadOnly}
        required={col.required && !isReadOnly}
        error={errors[col.field]}
        themeColor={themeColor}
        hideSelectLabel={true}
        showForeignKeyInfo={col.field.endsWith('Id_Tabelle')}
      />
    );
  }, [formData, handleInputChange, inputTypes, lookupOptions, isLoadingLookups, isProcessing, errors, themeColor]);

  const renderColumnSection = useCallback((section, themeColor) => {
    if (section.layout !== 'twoColumns') {
      return (
        <div className="space-y-4">
          {section.fields.map(field => {
            if (Array.isArray(field)) {
              return (
                <div key={field.join('-')} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {field.map(fieldName => {
                    const col = formColumnProps.find(c => c.field === fieldName);
                    if (!col) return null;
                    return (
                      <div key={fieldName}>
                        {renderFormInput(col)}
                      </div>
                    );
                  })}
                </div>
              );
            } else {
              const col = formColumnProps.find(c => c.field === field);
              if (!col) return null;
              return renderFormInput(col);
            }
          })}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {section.columns.map((column, columnIndex) => (
          <div key={columnIndex} className="space-y-4">
            <div className={`flex items-center space-x-2 p-3 bg-${themeColor}-50 dark:bg-${themeColor}-900/20 rounded-lg border border-${themeColor}-200 dark:border-${themeColor}-700`}>
              {column.icon === 'FaArrowRight' && <FaArrowRight className={`h-4 w-4 text-${themeColor}-600 dark:text-${themeColor}-400`} />}
              {column.icon === 'FaArrowLeft' && <FaArrowLeft className={`h-4 w-4 text-${themeColor}-600 dark:text-${themeColor}-400`} />}
              <h4 className={`text-sm font-semibold text-${themeColor}-800 dark:text-${themeColor}-200`}>
                {column.title}
              </h4>
            </div>
            
            <div className="space-y-3">
              {column.fields.map((field, fieldIndex) => {
                if (Array.isArray(field)) {
                  return (
                    <div key={fieldIndex} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {field.map(fieldName => {
                        const col = formColumnProps.find(c => c.field === fieldName);
                        if (!col) return null;
                        return (
                          <div key={fieldName}>
                            {renderFormInput(col)}
                          </div>
                        );
                      })}
                    </div>
                  );
                } else {
                  const col = formColumnProps.find(c => c.field === field);
                  if (!col) return null;
                  return (
                    <div key={fieldIndex}>
                      {renderFormInput(col)}
                    </div>
                  );
                }
              })}
            </div>
          </div>
        ))}
      </div>
    );
  }, [formColumnProps, renderFormInput]);

  const renderFormContent = useCallback(() => {
    if (formLayoutConfig && formLayoutConfig.sections) {
      return (
        <div className="space-y-6">
          {formLayoutConfig.sections.map((section, index) => (
            <div key={index}>
              {section.title && (
                <h4 className={`text-md font-semibold text-${themeColor}-800 dark:text-${themeColor}-200 mb-4 ${
                  index > 0 ? 'mt-6 pt-4 border-t border-gray-200 dark:border-gray-600' : ''
                }`}>
                  {section.title}
                </h4>
              )}
              
              {renderColumnSection(section, themeColor)}
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4">
        {formColumnProps.map(col => renderFormInput(col))}
      </div>
    );
  }, [formColumnProps, formLayoutConfig, renderColumnSection, themeColor, renderFormInput]);

  // Effects DOPO gli hooks ma PRIMA del return condizionale
  useEffect(() => {
    if (selectedRow && Object.keys(selectedRow).length > 0) {
      // **FIX**: Inizializza i campi select mancanti con 0 invece di undefined
      const normalizedData = { ...selectedRow };
      columnProps.forEach(col => {
        if (inputTypes[col.field] === 'select' && (normalizedData[col.field] === undefined || normalizedData[col.field] === null || normalizedData[col.field] === '')) {
          normalizedData[col.field] = 0;
        }
      });
      setFormData(normalizedData);
      originalDataRef.current = normalizedData;
      setErrors({});
    } else {
      setFormData({});
      originalDataRef.current = {};
      setErrors({});
    }
  }, [selectedRow, columnProps, inputTypes]);

  // ADESSO il return condizionale può essere posizionato qui
  if (!selectedRow || Object.keys(selectedRow).length === 0) {
    return null; 
  }

  console.log('[EditRecordForm] Props received:', { lookupOptions, columnProps });
  const extraTabelleOpts = lookupOptions['extraTabelleByProductLookup'];
  console.log('[EditRecordForm] extraTabelleByProductLookup options object:', extraTabelleOpts);
  if(extraTabelleOpts) {
    console.log('[EditRecordForm] Actual options for extraTabelle:', extraTabelleOpts.options);
    console.log('[EditRecordForm] IsLoading for extraTabelle:', extraTabelleOpts.isLoading);
  }

  // ✅ MODIFICATO: Condizione per nascondere il pulsante degli attributi per SKE_Oggetti
  const shouldShowRelatedItemsButton = () => {
    // Nascondi il pulsante per SKE_Oggetti dato che ora usiamo RowActionPopup
    if (tableName === 'SKE_Oggetti') {
      return false;
    }
    
    // Per altre tabelle, mantieni la logica esistente se presente
    return typeof onViewRelatedItems === 'function';
  };

  return (
    <div ref={ref} className={`mt-6 mb-8 p-4 md:p-6 bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700`}>
      <div className={`
        flex justify-between items-center px-6 py-4 mb-6 bg-gradient-to-r 
        from-${themeColor}-100 to-${themeColor}-200 
        dark:from-${themeColor}-900/40 dark:to-${themeColor}-800/40 
        border-b-2 border-${themeColor}-300 dark:border-${themeColor}-700
      `}>
        <h3 className={`text-lg font-bold text-${themeColor}-800 dark:text-${themeColor}-100`}>
          Modifica Record (ID: {selectedRow[primaryKey]})
        </h3>
        {onMinimize && (
          <button
            type="button"
            onClick={onMinimize}
            className="p-1 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
            title="Minimizza Form"
          >
            <FaWindowMinimize />
          </button>
        )}
      </div>

      <form onSubmit={handleUpdateSubmit} noValidate className="p-4 md:p-6">
        {renderFormContent()}

        <div className={`mt-6 flex flex-col sm:flex-row justify-between items-center border-t pt-4 border-${themeColor}-200 dark:border-${themeColor}-800`}>
          {onDelete && (
            <button
              type="button"
              onClick={handleDeleteClick}
              disabled={isProcessing}
              className="px-4 py-2 text-sm border border-red-500 text-red-600 dark:text-red-400 dark:border-red-600 rounded-md hover:bg-red-50 dark:hover:bg-red-900/30 transition disabled:opacity-50 mb-2 sm:mb-0 flex items-center justify-center"
            >
              <FaTrash className="mr-1.5 h-4 w-4" />
              Elimina
            </button>
          )}
          
          <div className="flex flex-wrap items-center space-x-3">
            {onViewRelatedItems && shouldShowRelatedItemsButton() && (
              <button
                type="button"
                onClick={onViewRelatedItems}
                disabled={isProcessing}
                className={`px-4 py-2 text-sm border border-${themeColor}-500 text-${themeColor}-600 dark:text-${themeColor}-400 dark:border-${themeColor}-600 rounded-md hover:bg-${themeColor}-50 dark:hover:bg-${themeColor}-900/30 transition disabled:opacity-50 flex items-center justify-center`}
              >
                <FaExternalLinkAlt className="mr-1.5 h-4 w-4" />
                {relatedItemsButtonText}
              </button>
            )}
            
            {/* NUOVO: Renderizza pulsanti custom se forniti */}
            {renderCustomFormActions && renderCustomFormActions(selectedRow)}
            
            <button
              type="button"
              onClick={onCancel}
              disabled={isProcessing}
              className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition disabled:opacity-50 flex items-center justify-center"
            >
              <FaTimes className="mr-1.5 inline h-4 w-4" />
              Annulla
            </button>
            <button
              type="submit"
              disabled={isProcessing || !Object.keys(findDifferences(originalDataRef.current, formData)).length}
              className={`px-4 py-2 text-sm bg-${themeColor}-600 text-white rounded-md hover:bg-${themeColor}-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center`}
            >
              {isProcessing ? (
                <FaSpinner className="animate-spin mr-1.5 h-4 w-4" />
              ) : (
                <FaSave className="mr-1.5 h-4 w-4" />
              )}
              Salva Modifiche
            </button>
          </div>
        </div>
      </form>
    </div>
  );
});

export default EditRecordForm;
